import type { EventDefinition } from '../types';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const DEFAULT_SEARCH_DAYS = 7;

function clampIntervalHours(intervalHours?: number): number {
  if (!intervalHours || intervalHours <= 0) {
    return 1;
  }
  return intervalHours;
}

function normalizeHour(value?: number): number {
  const hour = Number.isFinite(value) ? Math.trunc(value as number) : NaN;
  if (Number.isNaN(hour)) {
    return 0;
  }
  const normalized = hour % 24;
  return normalized < 0 ? normalized + 24 : normalized;
}

function getSortedStartMinutes(event: EventDefinition): number[] {
  return Array.from(
    new Set(event.startMinutes.filter(minute => minute >= 0 && minute < 60))
  ).sort((a, b) => a - b);
}

function getUtcMidnightMs(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function getOccurrencesInRange(event: EventDefinition, windowStart: Date, windowEnd: Date): Date[] {
  const windowStartMs = windowStart.getTime();
  const windowEndMs = windowEnd.getTime();

  if (windowEndMs < windowStartMs) {
    return [];
  }

  // Handle custom times (irregular intervals throughout the day)
  if (event.customTimes && event.customTimes.length > 0) {
    const occurrences: Date[] = [];
    let currentDayMidnightMs = getUtcMidnightMs(windowStart);
    const finalDayMidnightMs = getUtcMidnightMs(windowEnd);

    while (currentDayMidnightMs <= finalDayMidnightMs) {
      for (const customTime of event.customTimes) {
        const hour = normalizeHour(customTime.hour);
        const minute = Math.max(0, Math.min(59, customTime.minute));
        const occurrenceMs = currentDayMidnightMs + hour * MS_PER_HOUR + minute * MS_PER_MINUTE;

        if (occurrenceMs >= windowStartMs && occurrenceMs <= windowEndMs) {
          occurrences.push(new Date(occurrenceMs));
        }
      }
      currentDayMidnightMs += MS_PER_DAY;
    }

    return occurrences.sort((a, b) => a.getTime() - b.getTime());
  }

  // Original logic for regular intervals
  const startMinutes = getSortedStartMinutes(event);

  if (startMinutes.length === 0) {
    return [];
  }

  const intervalHours = clampIntervalHours(event.intervalHours);
  const firstHour = normalizeHour(event.startHour);
  const occurrences: Date[] = [];
  let currentDayMidnightMs = getUtcMidnightMs(windowStart);
  const finalDayMidnightMs = getUtcMidnightMs(windowEnd);

  outer: while (currentDayMidnightMs <= finalDayMidnightMs) {
    for (let hour = firstHour; hour < 24; hour += intervalHours) {
      const hourMs = currentDayMidnightMs + hour * MS_PER_HOUR;

      for (const minute of startMinutes) {
        const occurrenceMs = hourMs + minute * MS_PER_MINUTE;

        if (occurrenceMs < windowStartMs) {
          continue;
        }

        if (occurrenceMs > windowEndMs) {
          break outer;
        }

        occurrences.push(new Date(occurrenceMs));
      }
    }

    currentDayMidnightMs += MS_PER_DAY;
  }

  return occurrences;
}

export function getNow(): Date {
  return new Date();
}

/**
 * Calculates the next event start time in UTC (matching Guild Wars 2 server time).
 */
export function getNextStartUtc(event: EventDefinition, now = getNow()): Date {
  const searchWindowEnd = new Date(now.getTime() + DEFAULT_SEARCH_DAYS * MS_PER_DAY);
  const occurrences = getOccurrencesInRange(event, now, searchWindowEnd);

  if (occurrences.length > 0) {
    return occurrences[0];
  }

  console.warn(`Could not find next start time for event ${event.id}, using fallback`);
  return new Date(now.getTime() + clampIntervalHours(event.intervalHours) * MS_PER_HOUR);
}

/**
 * Calculates the most recent event start time in UTC (matching Guild Wars 2 server time).
 */
export function getMostRecentStartUtc(event: EventDefinition, now = getNow()): Date {
  const searchWindowStart = new Date(now.getTime() - DEFAULT_SEARCH_DAYS * MS_PER_DAY);
  const occurrences = getOccurrencesInRange(event, searchWindowStart, now);

  if (occurrences.length > 0) {
    return occurrences[occurrences.length - 1];
  }

  console.warn(`Could not find most recent start time for event ${event.id}, using fallback`);
  const fallbackInterval = clampIntervalHours(event.intervalHours) * MS_PER_HOUR;
  const fallbackDuration = event.durationMinutes * MS_PER_MINUTE;
  const fallbackOffset = Math.max(fallbackInterval, fallbackDuration + MS_PER_MINUTE);
  return new Date(now.getTime() - fallbackOffset);
}

export function isEventRunning(event: EventDefinition, now = getNow()): boolean {
  const start = getMostRecentStartUtc(event, now);
  const end = new Date(start.getTime() + event.durationMinutes * MS_PER_MINUTE);
  return now >= start && now < end;
}

export function msUntil(date: Date, now = getNow()): number {
  return Math.max(0, date.getTime() - now.getTime());
}

export function formatClock(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export function formatTimeLeft(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Gets the next UTC midnight (server reset time)
 */
export function getNextUtcMidnight(now = getNow()): Date {
  const nextMidnight = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0, 0
  ));
  return nextMidnight;
}

/**
 * Formats a date as UTC time string (HH:MM:SS)
 */
export function formatUtcTime(date: Date): string {
  const h = String(date.getUTCHours()).padStart(2, '0');
  const m = String(date.getUTCMinutes()).padStart(2, '0');
  const s = String(date.getUTCSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/**
 * Formats time until next UTC midnight as HH:MM:SS
 */
export function formatTimeUntilReset(now = getNow()): string {
  const nextMidnight = getNextUtcMidnight(now);
  const msUntilReset = msUntil(nextMidnight, now);
  const totalSeconds = Math.floor(msUntilReset / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function getProgress(event: EventDefinition, now = getNow()): number {
  const start = getMostRecentStartUtc(event, now);
  const end = new Date(start.getTime() + event.durationMinutes * MS_PER_MINUTE);
  if (now <= start) return 0;
  if (now >= end) return 1;
  const span = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  return Math.min(1, Math.max(0, elapsed / span));
}

export function getUpcomingOccurrences(event: EventDefinition, now = getNow(), lookaheadHours: number): Date[] {
  const lookaheadEnd = new Date(now.getTime() + lookaheadHours * MS_PER_HOUR);
  return getOccurrencesInRange(event, now, lookaheadEnd);
}

// Tyrian Time Utilities
// A single day-night cycle lasts for 2 hours in real time (120 minutes)
// In-universe it is considered to take 24 hours
// 1 Tyrian hour = 5 real minutes
// 1 Tyrian minute = 5 real seconds

const TYRIAN_CYCLE_MINUTES = 120; // 2 hours in real time
const REAL_MINUTES_PER_TYRIAN_HOUR = 5;
const REAL_SECONDS_PER_TYRIAN_MINUTE = 5;

// GW2 Tyrian time offset: The cycle may not align with UTC hour boundaries
// This offset can be adjusted if the calculated time doesn't match in-game time
// Common offsets: 0 (aligned with UTC hours), or a specific minute offset
const TYRIAN_TIME_OFFSET_MINUTES = 0; // Adjust this if time is consistently off

export type TyrianStage = 'dawn' | 'day' | 'dusk' | 'night';

export interface TyrianTime {
  hours: number;
  minutes: number;
  stage: TyrianStage;
  progress: number; // 0-1 progress through the full cycle
  stageProgress: number; // 0-1 progress through current stage
}

/**
 * Converts real time to Tyrian time.
 * The cycle resets every 2 hours (120 minutes) of real time.
 * GW2 Tyrian time is based on UTC time, with the cycle repeating every 2 hours.
 * Note: GW2's Tyrian time cycle may have a specific offset from UTC midnight.
 */
export function getTyrianTime(now = getNow()): TyrianTime {
  // Get total seconds since UTC midnight
  const totalSeconds = now.getUTCHours() * 3600 + 
                       now.getUTCMinutes() * 60 + 
                       now.getUTCSeconds() + 
                       now.getUTCMilliseconds() / 1000;
  
  // Convert to minutes and get position in 2-hour cycle (120 minutes = 7200 seconds)
  // GW2's cycle might not align with UTC hour boundaries, so we apply an offset
  // The cycle repeats every 2 hours, so we calculate position within the current 2-hour window
  const totalMinutes = totalSeconds / 60;
  const adjustedMinutes = totalMinutes + TYRIAN_TIME_OFFSET_MINUTES;
  const cyclePosition = adjustedMinutes % TYRIAN_CYCLE_MINUTES;
  // Ensure cyclePosition is positive
  const normalizedCyclePosition = cyclePosition < 0 ? cyclePosition + TYRIAN_CYCLE_MINUTES : cyclePosition;
  
  // Calculate Tyrian hours and minutes
  // Each Tyrian hour is 5 real minutes (300 seconds)
  // Each Tyrian minute is 5 real seconds
  // Tyrian time starts at 00:00
  const tyrianHoursRaw = Math.floor(normalizedCyclePosition / REAL_MINUTES_PER_TYRIAN_HOUR);
  const tyrianHours = tyrianHoursRaw % 24; // Start at 00:00, wrap at 24
  
  // Calculate Tyrian minutes: remaining seconds in current Tyrian hour / 5
  const remainingSecondsInHour = (normalizedCyclePosition % REAL_MINUTES_PER_TYRIAN_HOUR) * 60;
  const tyrianMinutes = Math.floor(remainingSecondsInHour / REAL_SECONDS_PER_TYRIAN_MINUTE);
  
  // Calculate gradient position first to determine stage
  // This ensures stage is perfectly synchronized with the gradient
  const tyrianTotalMinutes = tyrianHours * 60 + tyrianMinutes;
  let gradientPos: number;
  
  // Map Tyrian time to gradient position (same logic as getGradientPosition)
  if (tyrianTotalMinutes >= 0 && tyrianTotalMinutes < 300) {
    // Night: 00:00-05:00 → 0-18%
    gradientPos = (tyrianTotalMinutes / 300) * 18;
  } else if (tyrianTotalMinutes >= 300 && tyrianTotalMinutes < 360) {
    // Dawn: 05:00-06:00 → 18-26%
    gradientPos = 18 + ((tyrianTotalMinutes - 300) / 60) * 8;
  } else if (tyrianTotalMinutes >= 360 && tyrianTotalMinutes < 1200) {
    // Day: 06:00-20:00 → 26-87%
    gradientPos = 26 + ((tyrianTotalMinutes - 360) / 840) * 61;
  } else if (tyrianTotalMinutes >= 1200 && tyrianTotalMinutes < 1260) {
    // Dusk: 20:00-21:00 → 87-93%
    gradientPos = 87 + ((tyrianTotalMinutes - 1200) / 60) * 6;
  } else {
    // Night: 21:00-24:00 → 93-100%
    gradientPos = 93 + ((tyrianTotalMinutes - 1260) / 180) * 7;
  }
  
  // Determine stage based on gradient position to ensure perfect sync
  // Stage boundaries match gradient color percentages:
  // Night: 0-18% and 93-100%
  // Dawn: 18-26%
  // Day: 26-87%
  // Dusk: 87-93%
  let stage: TyrianStage;
  let stageProgress: number;
  
  if (gradientPos >= 0 && gradientPos < 18) {
    // Night: 0-18% (00:00-05:00)
    stage = 'night';
    stageProgress = gradientPos / 18; // 0-1 over 18% gradient
  } else if (gradientPos >= 18 && gradientPos < 26) {
    // Dawn: 18-26% (05:00-06:00)
    stage = 'dawn';
    stageProgress = (gradientPos - 18) / 8; // 0-1 over 8% gradient
  } else if (gradientPos >= 26 && gradientPos < 87) {
    // Day: 26-87% (06:00-20:00)
    stage = 'day';
    stageProgress = (gradientPos - 26) / 61; // 0-1 over 61% gradient
  } else if (gradientPos >= 87 && gradientPos < 93) {
    // Dusk: 87-93% (20:00-21:00)
    stage = 'dusk';
    stageProgress = (gradientPos - 87) / 6; // 0-1 over 6% gradient
  } else {
    // Night: 93-100% (21:00-24:00)
    stage = 'night';
    stageProgress = (gradientPos - 93) / 7; // 0-1 over 7% gradient
  }
  
  return {
    hours: tyrianHours,
    minutes: tyrianMinutes,
    stage,
    progress: normalizedCyclePosition / TYRIAN_CYCLE_MINUTES,
    stageProgress,
  };
}

/**
 * Formats Tyrian time as HH:MM (24-hour format)
 */
export function formatTyrianTime(tyrianTime: TyrianTime): string {
  const hours = String(tyrianTime.hours).padStart(2, '0');
  const minutes = String(tyrianTime.minutes).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Gets the stage label (capitalized)
 */
export function getStageLabel(stage: TyrianStage): string {
  return stage.charAt(0).toUpperCase() + stage.slice(1);
}

/**
 * Converts Tyrian time to gradient position (0-100%)
 * Maps Tyrian time to the gradient color percentages:
 * - Night: 0-18% (00:00-05:00) and 93-100% (21:00-24:00)
 * - Dawn: 18-26% (05:00-06:00)
 * - Day: 26-87% (06:00-20:00)
 * - Dusk: 87-93% (20:00-21:00)
 */
export function getGradientPosition(tyrianTime: TyrianTime): number {
  const tyrianTotalMinutes = tyrianTime.hours * 60 + tyrianTime.minutes;
  
  // Map Tyrian time (0-1440 minutes) to gradient position (0-100%)
  if (tyrianTotalMinutes >= 0 && tyrianTotalMinutes < 300) {
    // Night: 00:00-05:00 → 0-18%
    return (tyrianTotalMinutes / 300) * 18;
  } else if (tyrianTotalMinutes >= 300 && tyrianTotalMinutes < 360) {
    // Dawn: 05:00-06:00 → 18-26%
    return 18 + ((tyrianTotalMinutes - 300) / 60) * 8;
  } else if (tyrianTotalMinutes >= 360 && tyrianTotalMinutes < 1200) {
    // Day: 06:00-20:00 → 26-87%
    return 26 + ((tyrianTotalMinutes - 360) / 840) * 61;
  } else if (tyrianTotalMinutes >= 1200 && tyrianTotalMinutes < 1260) {
    // Dusk: 20:00-21:00 → 87-93%
    return 87 + ((tyrianTotalMinutes - 1200) / 60) * 6;
  } else {
    // Night: 21:00-24:00 → 93-100%
    return 93 + ((tyrianTotalMinutes - 1260) / 180) * 7;
  }
}

// Cantha Time Utilities
// A single day-night cycle lasts for 2 hours in real time (120 minutes)
// In-universe it is considered to take 24 hours
// 1 Cantha hour = 5 real minutes
// 1 Cantha minute = 5 real seconds

const CANTHA_CYCLE_MINUTES = 120; // 2 hours in real time
const REAL_MINUTES_PER_CANTHA_HOUR = 5;
const REAL_SECONDS_PER_CANTHA_MINUTE = 5;

// GW2 Cantha time offset: The cycle may not align with UTC hour boundaries
const CANTHA_TIME_OFFSET_MINUTES = 0; // Adjust this if time is consistently off

export type CanthaStage = 'dawn' | 'day' | 'dusk' | 'night';

export interface CanthaTime {
  hours: number;
  minutes: number;
  stage: CanthaStage;
  progress: number; // 0-1 progress through the full cycle
  stageProgress: number; // 0-1 progress through current stage
}

/**
 * Converts real time to Cantha time.
 * The cycle resets every 2 hours (120 minutes) of real time.
 * Cantha time stages:
 * - Day: 55 minutes (08:00-19:00)
 * - Dusk: 5 minutes (19:00-20:00)
 * - Night: 55 minutes (20:00-07:00)
 * - Dawn: 5 minutes (07:00-08:00)
 */
export function getCanthaTime(now = getNow()): CanthaTime {
  // Get total seconds since UTC midnight
  const totalSeconds = now.getUTCHours() * 3600 + 
                       now.getUTCMinutes() * 60 + 
                       now.getUTCSeconds() + 
                       now.getUTCMilliseconds() / 1000;
  
  // Convert to minutes and get position in 2-hour cycle
  const totalMinutes = totalSeconds / 60;
  const adjustedMinutes = totalMinutes + CANTHA_TIME_OFFSET_MINUTES;
  const cyclePosition = adjustedMinutes % CANTHA_CYCLE_MINUTES;
  const normalizedCyclePosition = cyclePosition < 0 ? cyclePosition + CANTHA_CYCLE_MINUTES : cyclePosition;
  
  // Calculate Cantha hours and minutes
  // Each Cantha hour is 5 real minutes (300 seconds)
  // Each Cantha minute is 5 real seconds
  // Cantha time starts at 00:00
  const canthaHoursRaw = Math.floor(normalizedCyclePosition / REAL_MINUTES_PER_CANTHA_HOUR);
  const canthaHours = canthaHoursRaw % 24; // Start at 00:00, wrap at 24
  
  // Calculate Cantha minutes: remaining seconds in current Cantha hour / 5
  const remainingSecondsInHour = (normalizedCyclePosition % REAL_MINUTES_PER_CANTHA_HOUR) * 60;
  const canthaMinutes = Math.floor(remainingSecondsInHour / REAL_SECONDS_PER_CANTHA_MINUTE);
  
  // Calculate gradient position first to determine stage
  // This ensures stage is perfectly synchronized with the gradient
  // Cantha stages in real time (minutes from cycle start):
  // Night: 0-35 (00:00-07:00 Cantha) and 100-120 (20:00-24:00 Cantha) = 55 total
  // Dawn: 35-40 (07:00-08:00 Cantha) = 5 minutes
  // Day: 40-95 (08:00-19:00 Cantha) = 55 minutes
  // Dusk: 95-100 (19:00-20:00 Cantha) = 5 minutes
  
  // Gradient mapping based on user's breakdown:
  // Night: 0-23% (00:00-07:00) and 79-100% (20:00-24:00)
  // Dawn: 23-29% (07:00-08:00)
  // Day: 29-72% (08:00-19:00)
  // Dusk: 72-79% (19:00-20:00)
  
  // Use normalizedCyclePosition (real time minutes 0-120) to map to gradient
  let gradientPos: number;
  
  if (normalizedCyclePosition >= 0 && normalizedCyclePosition < 35) {
    // Night: 0-35 real minutes (00:00-07:00 Cantha) → 0-23%
    gradientPos = (normalizedCyclePosition / 35) * 23;
  } else if (normalizedCyclePosition >= 35 && normalizedCyclePosition < 40) {
    // Dawn: 35-40 real minutes (07:00-08:00 Cantha) → 23-29%
    gradientPos = 23 + ((normalizedCyclePosition - 35) / 5) * 6;
  } else if (normalizedCyclePosition >= 40 && normalizedCyclePosition < 95) {
    // Day: 40-95 real minutes (08:00-19:00 Cantha) → 29-72%
    gradientPos = 29 + ((normalizedCyclePosition - 40) / 55) * 43;
  } else if (normalizedCyclePosition >= 95 && normalizedCyclePosition < 100) {
    // Dusk: 95-100 real minutes (19:00-20:00 Cantha) → 72-79%
    gradientPos = 72 + ((normalizedCyclePosition - 95) / 5) * 7;
  } else {
    // Night: 100-120 real minutes (20:00-24:00 Cantha) → 79-100%
    gradientPos = 79 + ((normalizedCyclePosition - 100) / 20) * 21;
  }
  
  // Determine stage based on gradient position to ensure perfect sync
  // Stage boundaries match gradient color percentages:
  // Night: 0-23% and 79-100%
  // Dawn: 23-29%
  // Day: 29-72%
  // Dusk: 72-79%
  let stage: CanthaStage;
  let stageProgress: number;
  
  if (gradientPos >= 0 && gradientPos < 23) {
    // Night: 0-23% (00:00-07:00)
    stage = 'night';
    stageProgress = gradientPos / 23; // 0-1 over 23% gradient
  } else if (gradientPos >= 23 && gradientPos < 29) {
    // Dawn: 23-29% (07:00-08:00)
    stage = 'dawn';
    stageProgress = (gradientPos - 23) / 6; // 0-1 over 6% gradient
  } else if (gradientPos >= 29 && gradientPos < 72) {
    // Day: 29-72% (08:00-19:00)
    stage = 'day';
    stageProgress = (gradientPos - 29) / 43; // 0-1 over 43% gradient
  } else if (gradientPos >= 72 && gradientPos < 79) {
    // Dusk: 72-79% (19:00-20:00)
    stage = 'dusk';
    stageProgress = (gradientPos - 72) / 7; // 0-1 over 7% gradient
  } else {
    // Night: 79-100% (20:00-24:00)
    stage = 'night';
    stageProgress = (gradientPos - 79) / 21; // 0-1 over 21% gradient
  }
  
  return {
    hours: canthaHours,
    minutes: canthaMinutes,
    stage,
    progress: normalizedCyclePosition / CANTHA_CYCLE_MINUTES,
    stageProgress,
  };
}

/**
 * Formats Cantha time as HH:MM (24-hour format)
 */
export function formatCanthaTime(canthaTime: CanthaTime): string {
  const hours = String(canthaTime.hours).padStart(2, '0');
  const minutes = String(canthaTime.minutes).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Gets the Cantha stage label (capitalized)
 */
export function getCanthaStageLabel(stage: CanthaStage): string {
  return stage.charAt(0).toUpperCase() + stage.slice(1);
}

/**
 * Converts Cantha time to gradient position (0-100%)
 * Maps Cantha time to the gradient color percentages based on real-time cycle position:
 * - Night: 0-23% (00:00-07:00 Cantha, 0-35 real minutes) and 79-100% (20:00-24:00 Cantha, 100-120 real minutes)
 * - Dawn: 23-29% (07:00-08:00 Cantha, 35-40 real minutes)
 * - Day: 29-72% (08:00-19:00 Cantha, 40-95 real minutes)
 * - Dusk: 72-79% (19:00-20:00 Cantha, 95-100 real minutes)
 */
export function getCanthaGradientPosition(now = getNow()): number {
  // Get real-time cycle position (0-120 minutes)
  const totalSeconds = now.getUTCHours() * 3600 + 
                       now.getUTCMinutes() * 60 + 
                       now.getUTCSeconds() + 
                       now.getUTCMilliseconds() / 1000;
  const totalMinutes = totalSeconds / 60;
  const adjustedMinutes = totalMinutes + CANTHA_TIME_OFFSET_MINUTES;
  const cyclePosition = adjustedMinutes % CANTHA_CYCLE_MINUTES;
  const normalizedCyclePosition = cyclePosition < 0 ? cyclePosition + CANTHA_CYCLE_MINUTES : cyclePosition;
  
  // Map real-time cycle position (0-120 minutes) to gradient position (0-100%)
  if (normalizedCyclePosition >= 0 && normalizedCyclePosition < 35) {
    // Night: 0-35 real minutes (00:00-07:00 Cantha) → 0-23%
    return (normalizedCyclePosition / 35) * 23;
  } else if (normalizedCyclePosition >= 35 && normalizedCyclePosition < 40) {
    // Dawn: 35-40 real minutes (07:00-08:00 Cantha) → 23-29%
    return 23 + ((normalizedCyclePosition - 35) / 5) * 6;
  } else if (normalizedCyclePosition >= 40 && normalizedCyclePosition < 95) {
    // Day: 40-95 real minutes (08:00-19:00 Cantha) → 29-72%
    return 29 + ((normalizedCyclePosition - 40) / 55) * 43;
  } else if (normalizedCyclePosition >= 95 && normalizedCyclePosition < 100) {
    // Dusk: 95-100 real minutes (19:00-20:00 Cantha) → 72-79%
    return 72 + ((normalizedCyclePosition - 95) / 5) * 7;
  } else {
    // Night: 100-120 real minutes (20:00-24:00 Cantha) → 79-100%
    return 79 + ((normalizedCyclePosition - 100) / 20) * 21;
  }
}
